import * as Sentry from "@sentry/node";
import { createServerFn } from "@tanstack/react-start";
import { useSession } from "@tanstack/react-start/server";
import { commitMutation, Environment, graphql } from "relay-runtime";
import { z } from "zod";
import { relayNetwork } from "@/relay/network";
import { type SessionData, sessionOptions } from "@/utils/getSession";
import type { createAccountMutation } from "./__generated__/createAccountMutation.graphql";

export const createAccount = createServerFn({ method: "POST" })
	.inputValidator(
		z.object({
			code: z.string().optional(),
			username: z.string().min(2),
			phone: z.string(),
			email: z.string().email(),
			password: z.string().min(1),
		}),
	)
	.handler(async ({ data }): Promise<Partial<SessionData>> => {
		try {
			// biome-ignore lint/correctness/useHookAtTopLevel: this is not a hook
			const session = await useSession(sessionOptions);

			return new Promise((resolve, reject) => {
				commitMutation<createAccountMutation>(
					new Environment({
						network: relayNetwork,
					}),
					{
						mutation: graphql`
  			    mutation createAccountMutation($payload: CreateAccountInput!) {
  							createAccount(payload: $payload) {
     							accessToken
     							refreshToken
     							expiresIn
     							user {
     					      id
                    email
                    username
                    displayName
                    avatarFallback
                    photo {
                        signedUrl
                    }
                    rawID
  							}
  						}
  					}
  				`,
						variables: {
							payload: {
								username: data.username,
								invite: data.code,
								email: data.email,
								password: data.password,
								whatsapp: data.phone,
							},
						},
						onCompleted: (response, errors) => {
							if (errors?.length) {
								reject(new Error(errors[0].message));
								return;
							}
							session
								.update({
									...session.data,
									isLoggedIn: true,
									accessToken: response.createAccount.accessToken,
									refreshToken: response.createAccount.refreshToken,
									user: response.createAccount.user,
									expiresAt: new Date(
										Date.now() +
											response.createAccount.expiresIn * 1000 -
											15 * 60 * 1000,
									).toISOString(),
								})
								.then(() => resolve(session.data))
								.catch(reject);
						},
						onError: (
							err: Error & { source?: { errors?: { message: string }[] } },
						) => {
							Sentry.captureException(err);

							if ("source" in err && err.source?.errors?.[0]) {
								reject(new Error(err.source?.errors?.[0].message));
							} else {
								reject(err);
							}
						},
					},
				);
			});
		} catch (err) {
			Sentry.captureException(err);

			throw err;
		}
	});
